import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { ToggleableNameValuePair } from '../models/name-value-pair.model';
import { AuthService } from '../../auth/services/auth.service';
import { Category } from '../models/category.enum';
import { Category as CategoryType } from '../models/category.model';
import { SimpleStatisticsResponse } from '../models/simple-statistics-response.model';
import { DataFilters, DataFiltersService } from './data-filters.service';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  of,
  lastValueFrom,
} from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { additionalFreemiumCategories, constants } from '../../../constants';
import { CategoriesResponse, CustomCategory } from '../models/category.model';

const categoryData = {
  [Category.MAKEUP]: 'spendingMakeup',
  [Category.FRAGRANCES]: 'spendingFragrances',
  [Category.SKIN_CARE]: 'spendingSkinCare',
  [Category.HAIR_CARE]: 'spendingHairCare',
  [Category.HYGIENE]: 'spendingHygiene',
  [Category.PERSONAL_CARE]: 'spendingAllPersonalCare',
  [Category.BEAUTY]: 'spendingBeauty',
  [Category.OTHER_PERSONAL_CARE]: 'spendingOtherPersonalCare',
  [Category.SPIRITS]: 'spendingSpirits',
  [Category.MINERAL_WATER]: 'spendingMineralWaters',
  [Category.BEAUTY_SERVICES]: 'spendingBeautyServices',
  [Category.TOTAL_BEAUTY]: 'spendingTotalBeauty',
  [Category.FOOD_AND_BEVERAGES]: 'spendingFoodAndBeverages',
  [Category.ALCOHOLIC_BEVERAGES_TOBACCO]: 'spendingAlcoholicTobacco',
  [Category.HOUSING]: 'spendingHousing',
  [Category.CLOTHING_AND_FOOTWEAR]: 'spendingClothingFootwear',
  [Category.FURNISHINGS_HOUSEHOLD_EQUIPMENT]: 'spendingFurnishedHousehold',
  [Category.HEALTH]: 'spendingHealth',
  [Category.TRANSPORT]: 'spendingTransport',
  [Category.COMMUNICATIONS]: 'spendingCommunications',
  [Category.RECREATION_AND_CULTURE]: 'spendingRecreationCulture',
  [Category.EDUCATION]: 'spendingEducation',
  [Category.RESTAURANTS_AND_HOTELS]: 'spendingRestaurantsHotel',
  [Category.MISCELLANEOUS]: 'spendingMiscellaneous',
  [Category.CULTURAL_SERVICES]: 'spendingCulturalServices',
  [Category.JEWELRY]: 'spendingJewelry',
};

const customCategories: CustomCategory[] = [
  {
    childCodes: ['12.1.1', '12.1.3.1'],
    label: 'Total Beauty',
    code: 'total_beauty',
    parentCode: '12.1',
  },
];

@Injectable()
export class CategoryService implements OnDestroy {
  private _categories$ = new ReplaySubject<CategoriesResponse>(1);
  public categoriesDropdown$ = new BehaviorSubject<CategoriesResponse>([]);
  public categoriesChildren = new Map<string, CategoriesResponse>();
  public getCategories$ = new Subject<CategoryType | undefined>();
  private dataFilters: DataFilters = new DataFilters();
  private ngUnsubscribe: Subject<void> = new Subject();
  private coicopCodes: string[] = [];
  private _userCategories = new BehaviorSubject<ToggleableNameValuePair[]>([]);
  public categoriesLoaded = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private dataFiltersService: DataFiltersService,
    private http: HttpClient
  ) {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => (this.dataFilters = filters));
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(({ categories }) => {
        this.coicopCodes = categories;
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getUserCategories() {
    return this._userCategories.asObservable();
  }

  getCategoryData(data: SimpleStatisticsResponse): number {
    return (
      (this.dataFilters.currency
        ? data[categoryData[this.dataFilters.category]]
        : data[`${categoryData[this.dataFilters.category]}PPP`]) ?? 0
    );
  }

  async getUserCategory(searchCategoryCode?: string, isFreemium?: boolean) {
    return lastValueFrom(this.getCategories(searchCategoryCode, isFreemium));
  }

  private buildTree(data: CategoriesResponse) {
    let categories: Record<string, ToggleableNameValuePair> = {};
    let rootCategories: ToggleableNameValuePair[] = [
      { name: 'Total Spending', value: '0', disabled: false, innerGroups: [] },
    ];
    // Create a map of nodes
    data.forEach((item) => {
      categories[item.code!] = {
        name: item.label,
        value: item.code,
        innerGroups: [],
        disabled: false,
      };
    });
    // Link categories to their parents
    data.forEach((item) => {
      let parentCode = item.code!.substring(0, item.code!.lastIndexOf('.'));
      if (
        data.some(
          (r) =>
            customCategories.filter(
              (customCategory) => customCategory.code === r.code
            ).length
        )
      ) {
        let isCustomAdded = false;
        customCategories.forEach((customCategory) => {
          if ([...customCategory.childCodes].includes(item.code || '')) {
            categories[customCategory.code].innerGroups!.push(
              categories[item.code!]
            );
            isCustomAdded = true;
            return;
          }
        });
        if (isCustomAdded) {
          return;
        }
      }
      const currentCustomCategory = customCategories.find((customCategory) => {
        return customCategory.code === item.code;
      });
      if (currentCustomCategory) {
        parentCode = currentCustomCategory.parentCode;
      }
      while (
        parentCode &&
        !categories[parentCode] &&
        parentCode.includes('.')
      ) {
        parentCode = parentCode.substring(0, parentCode.lastIndexOf('.'));
      }
      if (parentCode && categories[parentCode]) {
        categories[parentCode].innerGroups!.push(categories[item.code!]);
      } else {
        rootCategories.push(categories[item.code!]);
      }
    });

    return rootCategories;
  }

  async getList(isFreemium = false) {
    let allowedCategories: CategoriesResponse = [];
    const dedupeCoicopCodes = this.coicopCodes.map((code) => {
      if (code.includes('.')) {
        return code.substring(0, code.lastIndexOf('.'));
      }
      return code;
    });
    if (dedupeCoicopCodes.some((item) => !item.includes('.'))) {
      allowedCategories = await this.getUserCategory('', isFreemium).then(
        (r) => {
          return r.filter((category) =>
            this.coicopCodes.includes(category.code || '')
          );
        }
      );
    }
    if (dedupeCoicopCodes.includes('total_beauty')) {
      allowedCategories.push({
        code: 'total_beauty',
        label: 'Total Beauty',
        childrenSize: 2,
      });
    }
    const categoryRequests = [...new Set(dedupeCoicopCodes)].map((entry) => {
      return this.getUserCategory(entry).then((r) => {
        return r.filter((category) =>
          this.coicopCodes.includes(category.code || '')
        );
      });
    });

    await Promise.all(categoryRequests).then((categoryResponse) =>
      categoryResponse.forEach((cat) => allowedCategories.push(...cat))
    );
    allowedCategories.sort((a, b) => {
      return b.code!.length - a.code!.length;
    });

    this._userCategories.next(this.buildTree(allowedCategories));
    this.categoriesLoaded.next(true);
  }

  getMainCategories() {
    return this._categories$.asObservable();
  }

  getCategoriesDropdown() {
    return this.categoriesDropdown$.asObservable();
  }

  public getCategories(
    searchCategoryCode?: string,
    isFreemium?: boolean
  ): Observable<CategoriesResponse> {
    if (searchCategoryCode && this.categoriesChildren.has(searchCategoryCode)) {
      return of(this.categoriesChildren.get(searchCategoryCode)!);
    }
    return this.http
      .get<CategoriesResponse>(
        `${
          isFreemium
            ? constants.uriParts.publicCoicopCategories
            : constants.uriParts.coicopCategories
        }${searchCategoryCode || 'root'}`
      )
      .pipe(
        map((res) => {
          const categories = res;
          if (isFreemium) {
            categories.push(...additionalFreemiumCategories);
          }
          return categories.sort((a, b) => a.label.localeCompare(b.label));
        }),
        tap((res) => {
          if (!searchCategoryCode) {
            const mainCategories: CategoriesResponse = [
              {
                code: undefined,
                label: 'All categories',
              },
              ...res,
            ];
            return this._categories$.next(mainCategories);
          }
          this.categoriesDropdown$.pipe(take(1)).subscribe((categories) => {
            //Push base categories if new category is hovered or category dropdown is empty
            if (
              categories.length === 0 ||
              !categories.some((category) =>
                category.code?.includes(searchCategoryCode)
              )
            ) {
              return this.categoriesDropdown$.next(res);
            }
            //Remove all children categories to hide dropdowns
            categories.forEach(
              (category) => (category.childrenCategories = [])
            );
            //Find parent category
            let newCategoryDropdownIndex = categories.findIndex(
              (c) => c.code === searchCategoryCode
            );
            if (categories[newCategoryDropdownIndex]) {
              categories[newCategoryDropdownIndex].childrenCategories = res;
            }
            //Push categories with childrens to dropdown
            this.categoriesDropdown$.next(categories);
          });
        })
      );
  }
}
